import { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';

// --- Constants ---
const maxChartWidth = 500;
const margin = { top: 0, right: 0, bottom: 0, left: 0 };
const barHeight = 25;
const barSpacing = 5;
const innerMargin = 20;
const labelVisibilityWidthThreshold = 80;
const labelVerticalShift = 5; 

// Colors
const rootNodeColor = "#dffbff";
const greysArray = ["#6b6b6b", "#787878", "#979797", "#b5b5b5"];
const bluesArray = ["#455a8c", "#646aa9", "#8e82ca", "#aba5dc"];
const whitesArray = ["#b5b5b5", "#c7c7c7", "#e3e3e3", "#ffffff"];
const colorsArray = [greysArray, bluesArray, whitesArray];

// --- Helper: Find Active Period ---
const getActivePeriod = (item, year) => {
    if (!item.application_periods || item.application_periods.length === 0) return null;
    return item.application_periods.find(p => 
        year >= p.start_year && (!p.end_year || year <= p.end_year)
    );
};

// --- Helper: Get Value (Absolute) ---
const getValue = (item, year, parentValue) => {
    const activePeriod = getActivePeriod(item, year);
    if (!activePeriod) return 0; // Si la règle n'existe pas cette année-là

    const val = activePeriod.value;
    if (val === null || val === undefined) return parentValue;

    if (item.value_unit === 'share') { // L'unité passe au niveau parent ou reste dans period selon votre choix
        return val * parentValue;
    }
    return val;
};

// --- Helper: Get Raw Rate (For Labels) ---
const getRawRate = (item, year) => {
    const activePeriod = getActivePeriod(item, year);
    return activePeriod ? activePeriod.value : 0;
};

const adjustLabelPositions = (nodes) => {
    const sorted = [...nodes].sort((a, b) => a.y - b.y);
    const adjusted = [];

    sorted.forEach((node, i) => {
        let newY = node.y + node.height / 2; 

        if (i > 0) {
            const prev = adjusted[i - 1];
            if (Math.abs(newY - prev.adjustedY) < labelVerticalShift &&
                Math.abs(node.x - prev.x) < 50) {
                newY = prev.adjustedY + labelVerticalShift;
            }
        }
        adjusted.push({ ...node, adjustedY: newY });
    });
    return adjusted;
};

const setLabelVisibility = (currentZoomScale, target) => {
    const labelElement = d3.select(target);
    const width = parseFloat(labelElement.attr("data-original-width"));
    const effectiveWidth = width * currentZoomScale;

    const fontSize = Math.min(Math.max(2, 10 / currentZoomScale), 8);
    labelElement.style("font-size", `${fontSize}px`);

    if (effectiveWidth >= labelVisibilityWidthThreshold || (currentZoomScale > 4 && effectiveWidth < labelVisibilityWidthThreshold)) {
        labelElement.style("visibility", "visible");
    } else {
        labelElement.style("visibility", "hidden");
    }
};

const handleMouseOver = (hoveredDataItem, layers) => {
    const defaultFilter = "blur(0px)";
    const fadedFilter = "blur(1px)";

    layers.bars.selectAll(".chart-bar").style("filter", fadedFilter);
    layers.bgs.selectAll(".chart-bar").style("filter", fadedFilter);

    const baseId = hoveredDataItem.id.replace(/^(bar-|bg-)/, '');
    layers.bars.select(`#bar-${baseId}`).style("filter", defaultFilter);
    layers.bgs.select(`#bg-${baseId}`).style("filter", defaultFilter);

    layers.labels.selectAll("text.chart-label").style("visibility", "hidden");

    if (hoveredDataItem.parentId) {
        const parentBaseId = hoveredDataItem.parentId;
        layers.bars.select(`#bar-${parentBaseId}`).style("filter", defaultFilter);
        layers.bgs.select(`#bg-${parentBaseId}`).style("filter", defaultFilter);

        layers.labels.select(`#text-bar-${parentBaseId}`)
            .style("visibility", "visible");

        layers.labels.select(`#text-bar-${parentBaseId} > .label-description`)
            .style("visibility", "visible");
    }

    const labelSelection = layers.labels.select(`#text-bar-${baseId}`);
    labelSelection.style("visibility", "visible");
    labelSelection.select(".label-description").style("visibility", "visible");
};

const handleMouseOut = (layers, currentZoomScale) => {
    layers.bars.selectAll(".chart-bar").style("filter", null);
    layers.bgs.selectAll(".chart-bar").style("filter", null);

    layers.labels.selectAll("text.chart-label")
        .style("filter", null)
        .each(function () {
            setLabelVisibility(currentZoomScale, this);
        });

    layers.labels.selectAll("tspan.label-description")
        .style("visibility", "hidden");
};

const generateRoundedRectPath = (x, y, width, height, radii) => {
    const tl = Math.max(0, radii.topLeft || 0);
    const tr = Math.max(0, radii.topRight || 0);
    const br = Math.max(0, radii.bottomRight || 0);
    const bl = Math.max(0, radii.bottomLeft || 0);
    if (width < 0) width = 0;
    if (height < 0) height = 0;

    return `M${x + tl},${y} h${width - tl - tr} a${tr},${tr} 0 0 1 ${tr},${tr} v${height - tr - br} a${br},${br} 0 0 1 ${-br},${br} h${-(width - br - bl)} a${bl},${bl} 0 0 1 ${-bl},${-bl} v${-(height - bl - tl)} a${tl},${tl} 0 0 1 ${tl},${-tl} Z`;
};

export function FinancingObligationsChart({ 
    data, 
    externalZoomScale, 
    onRuleClick, 
    selectedRuleId,
    parentRuleId,
    isPickingParent,
    selectedYear
}) {

    const [absRevenue, setAbsRevenue] = useState(0);

    const chartRef = useRef(null);
    const d3ElementsRef = useRef({
        svg: null,
        zoomableGroup: null,
        layersKH: { bgs: null, bars: null, labels: null }
    });

    const onRuleClickRef = useRef(onRuleClick);
    useEffect(() => {
        onRuleClickRef.current = onRuleClick;
    }, [onRuleClick]);

    const initD3Chart = useCallback(() => {
        if (!chartRef.current) return;
        const container = d3.select(chartRef.current);
        container.selectAll("svg").remove();

        const svgWidth = maxChartWidth + margin.left + margin.right * 5;
        const svg = container.append("svg")
            .attr("id", "financingObligationsViz")
            .attr("width", svgWidth)
            .attr("height", 1);

        d3ElementsRef.current.svg = svg;
        const zoomableGroup = svg.append("g").attr("class", "zoomable-group");
        d3ElementsRef.current.zoomableGroup = zoomableGroup;

        d3ElementsRef.current.layersKH.bgs = zoomableGroup.append("g").attr("class", "layer-bgs").attr("transform", `translate(${margin.left}, ${margin.top})`);
        d3ElementsRef.current.layersKH.bars = zoomableGroup.append("g").attr("class", "layer-bars").attr("transform", `translate(${margin.left}, ${margin.top})`);
        d3ElementsRef.current.layersKH.labels = zoomableGroup.append("g").attr("class", "layer-labels").attr("transform", `translate(${margin.left}, ${margin.top})`);
    }, []);

    const updateD3Chart = useCallback((flatData, currentYear, currentZoomScale) => {
        if (!d3ElementsRef.current.layersKH.bars || !flatData) return;

        const activeData = flatData.filter(item => {
            const activePeriod = getActivePeriod(item, currentYear);
            return activePeriod !== null && activePeriod !== undefined;
        });

        let globalDomainMax = 100;
        activeData.forEach((item) => {
        if (item.value_unit !== null && !item.parent_id) {
            const activePeriod = getActivePeriod(item, currentYear);
            const value = activePeriod ? activePeriod.value : 0;
            globalDomainMax = Math.max(globalDomainMax, value);
        }
    });

        if (absRevenue > 0) {
            globalDomainMax = parseFloat(absRevenue);
        }

        let globalYPosition = 0;
        const layoutData = [];

        const buildLayout = (
            itemId,
            xOffset,
            yOffset,
            parentValue,
            depth,
            parentColorIndex,
            stackIndex = 0,
            stackSize = 1,
            parentBarWidth = 0,
            parentUnit = null
        ) => {
            const item = activeData.find(d => d.id === itemId);
            if (!item) return 0;

            const currentUnit = item.value_unit;
            const absoluteValue = getValue(item, currentYear, parentValue);
            const isMismatch = depth > 0 && (currentUnit !== parentUnit && currentUnit !== 'share');
            
            const ruleId = item.id.toString();

            const xScale = d3.scaleLinear().domain([0, globalDomainMax]).range([0, maxChartWidth - (innerMargin * depth)]);
            const currentBarHeight = barHeight - (depth * 2);

            let barWidth = 0;
            if (isMismatch) {
                barWidth = currentBarHeight;
            } else {
                barWidth = Math.max(1, xScale(absoluteValue));
            }

            const colorArray = colorsArray[parentColorIndex % colorsArray.length];
            let barColor = colorArray[depth % colorArray.length] || "#ccc";
            
            if (selectedRuleId && (item.id === selectedRuleId)) {
                barColor = "#fde68a";
            } else if (depth === 0) {
                barColor = rootNodeColor;
            }

            const isStack = stackSize > 1;
            const isFirst = stackIndex === 0;
            const isLast = stackIndex === stackSize - 1;

            layoutData.push({
                type: 'bar',
                id: `bar-${ruleId}`,
                rawId: ruleId,
                parentId: item.parent_id, 
                dataItem: item,
                value: absoluteValue,
                x: depth === 0 ? xOffset + 3 : xOffset + Math.min((parentBarWidth - xScale(parentValue)) / 2, 3),
                y: yOffset,
                width: barWidth,
                height: currentBarHeight,
                color: barColor,
                depth,
                isStack, isFirst, isLast,
                isStacked: isStack,
                isStandalone: !isStack,
                isMismatch,
                unit: currentUnit
            });

            let currentHeightConsumed = currentBarHeight + Math.max(3, barSpacing - depth * 2);
            const nextX = depth === 0 ? xOffset + 3 : xOffset + Math.min((parentBarWidth - xScale(parentValue)) / 2, 3);

            const children = activeData.filter(d => d.parent_id === itemId);
            const groups = {};
            const groupOrder = []; 

            children.forEach(child => {
                const key = child.stack_id || `standalone-${child.id}`;
                if (!groups[key]) {
                    groups[key] = [];
                    groupOrder.push(key);
                }
                groups[key].push(child);
            });

            let hasChildren = false;

            groupOrder.forEach(key => {
                const groupItems = groups[key];
                const isGroupStacked = groupItems.length > 1 || (groupItems[0] && groupItems[0].stack_id); 

                if (isGroupStacked) {
                    let stackXCursor = 0;
                    let maxStackHeight = 0;
                    
                    groupItems.forEach((stackItem, idx) => {
                        const heightUsed = buildLayout(
                            stackItem.id,
                            nextX + stackXCursor,
                            yOffset + currentHeightConsumed,
                            absoluteValue,
                            depth + 1,
                            idx, 
                            idx, 
                            groupItems.length,
                            barWidth,
                            currentUnit
                        );

                        const siblingValue = getValue(stackItem, currentYear, absoluteValue);
                        const childScale = d3.scaleLinear().domain([0, globalDomainMax]).range([0, maxChartWidth - (innerMargin * (depth + 1))]);
                        stackXCursor += childScale(siblingValue);
                        maxStackHeight = Math.max(maxStackHeight, heightUsed);
                    });
                    currentHeightConsumed += maxStackHeight;
                    hasChildren = true;

                } else {
                    const singleItem = groupItems[0];
                    const heightUsed = buildLayout(
                        singleItem.id,
                        nextX,
                        yOffset + currentHeightConsumed,
                        absoluteValue,
                        depth + 1,
                        parentColorIndex,
                        0, 1,
                        barWidth,
                        currentUnit
                    );
                    currentHeightConsumed += heightUsed;
                    hasChildren = true;
                }
            });

            if (hasChildren) {
                layoutData.push({
                    type: 'background',
                    id: `bg-${ruleId}`,
                    parentId: item.parent_id,
                    x: nextX,
                    y: yOffset,
                    width: barWidth,
                    height: currentHeightConsumed,
                    color: depth === 0 ? rootNodeColor : barColor,
                    depth,
                    dataItem: item
                });
            }

            return currentHeightConsumed += Math.max(3, barSpacing - depth * 2);
        };

        const rootItems = activeData.filter(d => !d.parent_id);
        rootItems.forEach((rootRule, i) => {
            const initialUnit = rootRule.value_unit || 'M€';
            const heightUsed = buildLayout(rootRule.id, 0, globalYPosition, parseFloat(absRevenue) || globalDomainMax, 0, 0, 0, 1, 0, initialUnit);
            globalYPosition += heightUsed;
        });

        const { bgs: layerBgs, bars: layerBars, labels: layerLabels } = d3ElementsRef.current.layersKH;
        d3ElementsRef.current.svg.attr("height", globalYPosition + margin.top + margin.bottom);

        // 1. Backgrounds
        const bgs = layerBgs.selectAll("rect.group-background")
            .data(layoutData.filter(d => d.type === 'background').sort((a, b) => a.depth - b.depth), d => d.id);

        bgs.exit().remove();

        bgs.enter().append("rect")
            .attr("class", "chart-bar group-background")
            .attr("id", d => d.id)
            .attr("rx", d => 1 - (0.2 * d.depth))
            .attr("ry", d => 1 - (0.2 * d.depth))
            .merge(bgs)
            .attr("x", d => d.x).attr("y", d => d.y)
            .attr("width", d => d.width).attr("height", d => d.height)
            .attr("fill", d => d.color)
            .style("cursor", onRuleClick ? "pointer" : "default")
            .on("mouseover", (event, d) => handleMouseOver(d, d3ElementsRef.current.layersKH))
            .on("mouseout", () => handleMouseOut(d3ElementsRef.current.layersKH, currentZoomScale))
            .on("click", (event, d) => {
                if (onRuleClickRef.current) {
                    onRuleClickRef.current(d.dataItem);
                    event.stopPropagation();
                }
            });

        // 2. Bars
        const bars = layerBars.selectAll("path.chart-bar")
            .data(layoutData.filter(d => d.type === 'bar'), d => d.id);

        bars.exit().remove();

        bars.enter().append("path")
            .attr("class", d => `chart-bar ${d.isStack ? 'stacked-bar' : 'standalone-bar'}`)
            .attr("id", d => d.id)
            .merge(bars)
            .attr("d", d => {
                if (d.isMismatch) {
                    const r = d.height / 2;
                    const cx = d.x + r;
                    const cy = d.y + r;
                    return `M ${cx}, ${cy} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
                }
                const r = 1 - (d.depth * 0.2);
                const radii = {
                    topLeft: (d.isStack && !d.isFirst) ? 0 : r,
                    bottomLeft: (d.isStack && !d.isFirst) ? 0 : r,
                    topRight: (d.isStack && !d.isLast) ? 0 : r,
                    bottomRight: (d.isStack && !d.isLast) ? 0 : r,
                };
                return generateRoundedRectPath(d.x, d.y, d.width, d.height, radii);
            })
            .attr("fill", d => d.color)
            .classed("parent-rule-bar", d => d.dataItem.id === parentRuleId)
            .classed("pulsating", d => d.dataItem.id === parentRuleId && isPickingParent)
            .style("cursor", onRuleClick ? "pointer" : "default")
            .on("mouseover", (event, d) => handleMouseOver(d, d3ElementsRef.current.layersKH))
            .on("mouseout", () => handleMouseOut(d3ElementsRef.current.layersKH, currentZoomScale))
            .on("click", (event, d) => {
                if (onRuleClickRef.current) {
                    onRuleClickRef.current(d.dataItem);
                    event.stopPropagation();
                }
            });

        // 3. Labels
        const barNodes = layoutData.filter(d => d.type === 'bar');
        const adjustedLabelNodes = adjustLabelPositions(barNodes);

        const labels = layerLabels.selectAll("text.chart-label")
            .data(adjustedLabelNodes, d => d.id);

        labels.exit().remove();

        const labelsEnter = labels.enter().append("text")
            .attr("class", "chart-label")
            .attr("text-anchor", "start")
            .style("fill", "black")
            .style("pointer-events", "none");

        labelsEnter.merge(labels)
            .attr("id", d => `text-${d.id}`)
            .attr("x", d => d.x + (d.width / 10))
            .attr("y", d => d.adjustedY)
            .attr("dy", "0.35em")
            .attr("data-original-width", d => d.width)
            .each(function (d) {
                const textElement = d3.select(this);
                textElement.selectAll("*").remove();

                const name = d.dataItem.object || d.dataItem.rule;
                let valueText = "";

                if (d.depth === 0) {
                    if (absRevenue > 0) {
                        valueText = `${absRevenue}M€`;
                    } else if (d.dataItem.value_unit === 'M€') {
                        // On cherche la valeur dans la période active pour l'année en cours
                        const activePeriod = getActivePeriod(d.dataItem, selectedYear);
                        const value = activePeriod ? activePeriod.value : 0;
                        valueText = `${value}M€`;
                    } else {
                        valueText = `100%`;
                    }
                } else if (d.dataItem.value_unit === 'share') {
                    if (absRevenue > 0) {
                        valueText = `${d.value.toFixed(1)}M€`;
                    } else {
                        // FIX: Determine correct rate for the year, handling multi-year data
                        const rawRate = getRawRate(d.dataItem, selectedYear);
                        valueText = `${+parseFloat(((rawRate || 0) * 100).toFixed(2))}%`;
                    }
                } else {
                    valueText = `${d.value.toFixed(1)} ${d.dataItem.value_unit || ''}`;
                }

                textElement.append("tspan")
                    .attr("x", d.x + (d.width / 10))
                    .attr("dy", "0em")
                    .text(`${name}: ${valueText}`);

                if (d.dataItem.description) {
                    textElement.append("tspan")
                        .attr("class", "label-description")
                        .attr("x", d.x + (d.width / 10))
                        .attr("dy", "1.2em")
                        .style("font-size", "0.8em")
                        .text(d.dataItem.description);
                }
            });

        layerLabels.selectAll(".chart-label").each(function () {
            setLabelVisibility(currentZoomScale, this);
        });
    }, [absRevenue, selectedRuleId, parentRuleId, isPickingParent, onRuleClick, selectedYear]);

    useEffect(() => {
        initD3Chart();
    }, [initD3Chart]);

    useEffect(() => {
        if (data && Array.isArray(data)) {
            updateD3Chart(data, selectedYear, externalZoomScale);
        }
    }, [data, selectedYear, absRevenue, externalZoomScale, updateD3Chart]);

    return (
        <div className="financing-obligations-container module-section">
            <div className="financing-obligations-title-container">
                <div className="module-section-title">Obligations de financement</div>
                <div className="financing-obligations-options">
                    <div className="broadcaster-revenue-input">
                        <label className="broadcaster-revenue-input-label" htmlFor="broadcaster-revenue-input">Chiffre d'affaires (M€)</label>
                        <input
                            type="text"
                            id="broadcaster-revenue-input"
                            className="broadcaster-revenue-input-box"
                            value={absRevenue}
                            onChange={(e) => setAbsRevenue(Number(e.target.value))}
                        />
                    </div>
                </div>
            </div>
            <div ref={chartRef}></div>
        </div>
    );
}